import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Typography, Space, List, Spin, Row, Col } from "antd";
import ReactPlayer from "react-player";
import { apiCaller } from "src/util/apiCaller";
import {
  UserOutlined,
  CalendarOutlined,
  FolderOpenOutlined,
  PlayCircleOutlined,
  YoutubeOutlined,
  PauseCircleTwoTone,
  PaperClipOutlined,
} from "@ant-design/icons";
import GopYForm from "../GopYForm";

const { Title, Paragraph, Text } = Typography;

interface VideoDetail {
  idTulieu: number;
  tenTuLieu: string;
  moTa: string;
  tacGia: string;
  historicalPeriod: string;
  ngayDang: string;
  videoUrls: {
    id: number;
    tenFile: string;
    duongDanFile: string;
    moTa: string;
    tacGia: string;
    anhDaiDien?: string;
  }[];
  anhDaiDien: string;
}

const DetailVideo: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [video, setVideo] = useState<VideoDetail | null>(null);
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [currentVideoTitle, setCurrentVideoTitle] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [buttonText, setButtonText] = useState('Tải về');

  useEffect(() => {
    if (id) {
      fetchVideoDetail(id);
    }
  }, [id]);

  const fetchVideoDetail = async (id: string) => {
    setLoading(true);
    try {
      const response = await apiCaller({
        url: `/apipublic/tulieuhienthi/${id}`,
        method: "GET",
      });

      if (response?.data) {
        const doc: VideoDetail = {
          idTulieu: response.data.idTulieu,
          tenTuLieu: response.data.tenTuLieu,
          moTa: response.data.moTa,
          tacGia: response.data.tacGia,
          historicalPeriod: response.data.loaiTuLieu.tenLoaiTuLieu || "",
          ngayDang: response.data.ngayDang,
          videoUrls: response.data.listTuLieuFile || [],
          anhDaiDien: response.data.anhDaiDien,
        };
        const filteredVideoUrls = doc.videoUrls.filter(file => isVideoFile(file.duongDanFile));
        setVideo(doc);
  
        if (filteredVideoUrls.length > 0) {
          setCurrentVideo(filteredVideoUrls[0].duongDanFile);
          setCurrentVideoTitle(
            decodeURIComponent(filteredVideoUrls[0].tenFile.split(".")[0] || "")
          );
        }
      }
    } catch (error) {
      console.error("Error fetching video detail:", error);
    } finally {
      setLoading(false);
    }
  };

  const isVideoFile = (url: string) => {
    const videoExtensions = ['.mp4', '.mov', '.avi', '.wmv', '.mkv'];
    return videoExtensions.some(ext => url.endsWith(ext));
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!video) {
    return <div>Video không tồn tại.</div>;
  }

  const handleDownload = async (file: { duongDanFile: string; tenFile: string }) => {
    setIsLoading(true);
    setButtonText("Đang lấy file...");
  
    try {
      const response = await fetch(file.duongDanFile);
      if (!response.ok) throw new Error("Network response was not ok");
  
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.tenFile || "file";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setIsLoading(false);
      setButtonText("Tải về");
    }
  };
  

  const filteredVideoUrls = video.videoUrls.filter(file => isVideoFile(file.duongDanFile));

  return (
    <div className="QTI-content-detail">
      <Title level={4}><YoutubeOutlined style={{ color: "red" }} /> {" "}{video.tenTuLieu}</Title>
      <Space
        direction="vertical"
        size={16}
        style={{ marginBottom: 24 }}
        className="QTI-detail-page-author"
      >
        <Space direction="horizontal" size={0}>
          <UserOutlined />
          <Text strong>{video.tacGia}</Text>
          <Text type="secondary">
            <CalendarOutlined />{" "}
            {new Date(video.ngayDang).toLocaleDateString("vi-VN")}
          </Text>
          <Text type="secondary">
            <FolderOpenOutlined /> {video.historicalPeriod}
          </Text>
        </Space>
      </Space>
      <Paragraph strong style={{ fontSize: 16 }}>
        {video.moTa}
      </Paragraph>
      <div className="QTI-detail-video-player" >
        {currentVideo && (
          <ReactPlayer
            url={currentVideo}
            playing
            controls
            width="100%"
            height="100%"
            style={{ position: "absolute", top: 0, left: 0 }}
          />
        )}
      </div>
      {currentVideoTitle && (
        <Title level={5} style={{ marginTop: "16px" }}>
          <PauseCircleTwoTone size={14} /> {" "}{currentVideoTitle}
        </Title>
      )}
      <Title level={4} style={{ marginTop: "24px" }}>
        <YoutubeOutlined style={{ color: "red" }} /> {" "}Danh sách video
      </Title>
      <List
        bordered
        itemLayout="vertical"
        dataSource={filteredVideoUrls}
        renderItem={(file) => {
          const tenVideo = decodeURIComponent(file.tenFile.split(".")[0] || "");
          const isActive = currentVideo === file.duongDanFile;
          return (
            <List.Item
              style={{
                backgroundColor: isActive ? "#e9e9e9" : "inherit",
                fontWeight: isActive ? "bold" : "normal",
                padding: "10px",
              }}
            >
              <Row gutter={16} align="middle">
                {video.anhDaiDien && (
                  <>
                    <Col xs={6} sm={4}>
                      <img
                        width="100%"
                        height={60}
                        alt={tenVideo}
                        src={video.anhDaiDien}
                        style={{
                          objectFit: "cover",
                          borderRadius: "4px",
                          border: "1px solid #f1f1f1",
                        }}
                      />
                      <PlayCircleOutlined
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          fontSize: "24px",
                          color: "white",
                          backgroundColor: "rgba(0, 0, 0, 0.5)",
                          borderRadius: "50%",
                          padding: "6px",
                          zIndex: 10,
                        }}
                      />
                    </Col>
                    <Col xs={14} sm={16}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentVideo(file.duongDanFile);
                          setCurrentVideoTitle(tenVideo);
                        }}
                        style={{ wordBreak: "break-word" }}
                      >
                        <YoutubeOutlined style={{ color: "red" }} /> {" "}{tenVideo}
                      </a>
                      <br />
                      <UserOutlined />{" "}{file.tacGia}
                    </Col>
                    <Col xs={4} sm={4}>
                    <a
                      href="#"
                      onClick={(event) => {
                        event.preventDefault();
                        handleDownload(file);
                      }}
                      style={{ alignItems: 'right' }}
                    >
                      {buttonText}
                    </a>
                    </Col>
                  </>
                )}
              </Row>
            </List.Item>
          );
        }}
      />
      <GopYForm />
    </div>
  );
};

export default DetailVideo;
